import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { fadeInUp, staggerContainer } from "@/lib/constants";
import { useScrollToSection } from "@/hooks/useScrollToSection";

const casos = [
  {
    sector: "Distribución alimentaria",
    contexto: "+200 referencias, distribución en Levante",
    problema: "Sospecha de que parte del catálogo estaba dando pérdidas, pero el ERP no imputaba bien los costes logísticos por referencia.",
    analisis: "Pipeline SQL desde el ERP a un modelo de Power BI con reparto real de costes de transporte y almacenamiento por SKU. DAX para calcular margen neto con logística incluida.",
    solucion: "Dashboard de rentabilidad por referencia + informe automatizado de los 30 SKU con margen negativo y propuesta de ajuste de precios.",
    resultado: "Surtido y política de precios revisados en 3 meses",
    metrica: "+18%",
    metricaLabel: "beneficio neto"
  },
  {
    sector: "Industria manufacturera",
    contexto: "3 líneas de producción, 40 empleados",
    problema: "Decisiones de producción tomadas por costumbre. Sin datos cruzados de rendimiento por línea, turno y operario.",
    analisis: "Conexión a la base de datos de producción + integración de partes de fabricación en Excel. Modelo en estrella con dimensiones de línea, turno, producto y tiempo. KPIs OEE básicos.",
    solucion: "Dashboard de productividad en Power BI con detección automática de cuellos de botella y comparativa entre turnos.",
    resultado: "Mejora de productividad sin contratar más personal",
    metrica: "+25%",
    metricaLabel: "productividad"
  },
  {
    sector: "Agroalimentaria",
    contexto: "+500 referencias, distribución nacional",
    problema: "Exceso de stock en unas referencias y roturas constantes en otras. Compras a proveedor planificadas a ojo.",
    analisis: "Modelo de forecasting en Python (Prophet) sobre histórico de ventas por SKU, temporada y zona. Cálculo de stock óptimo, punto de pedido y días de cobertura.",
    solucion: "Tablero de planificación de compras con previsión semanal y alertas automáticas sobre referencias a reaprovisionar.",
    resultado: "Menos stock muerto y menos roturas de servicio",
    metrica: "-32%",
    metricaLabel: "stock inmovilizado"
  }
];

export const CasosExitoSection = () => {
  const handleContactClick = useScrollToSection("contacto");

  return (
    <section className="relative section-padding overflow-hidden" data-testid="casos-exito-section">
      <div className="spotlight-1 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <span className="text-indigo-400 font-medium text-sm uppercase tracking-wider">
              Casos reales
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-4">
              Problema · Análisis · <span className="text-gradient">Solución · Resultado</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto mt-4 text-base">
              Cómo se traduce el stack (SQL, Power BI, Python) en decisiones operativas con impacto en cuenta de resultados.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {casos.map((caso, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="h-full success-card border-slate-800 card-hover overflow-hidden" data-testid={`caso-card-${index}`}>
                  <CardContent className="p-6 md:p-8">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="inline-block px-3 py-1 rounded-full bg-indigo-600/20 text-indigo-400 text-xs font-medium">
                        {caso.sector}
                      </span>
                    </div>
                    <p className="text-slate-500 text-xs mb-4 italic">{caso.contexto}</p>

                    <div className="space-y-4 mb-6">
                      <div>
                        <p className="text-red-400 text-xs font-medium mb-1 font-mono tracking-wider">PROBLEMA</p>
                        <p className="text-slate-300 text-sm">{caso.problema}</p>
                      </div>
                      <div>
                        <p className="text-purple-400 text-xs font-medium mb-1 font-mono tracking-wider">ANÁLISIS</p>
                        <p className="text-slate-300 text-sm">{caso.analisis}</p>
                      </div>
                      <div>
                        <p className="text-cyan-400 text-xs font-medium mb-1 font-mono tracking-wider">SOLUCIÓN</p>
                        <p className="text-slate-300 text-sm">{caso.solucion}</p>
                      </div>
                      <div>
                        <p className="text-green-400 text-xs font-medium mb-1 font-mono tracking-wider">RESULTADO</p>
                        <p className="text-slate-300 text-sm">{caso.resultado}</p>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-slate-700 text-center">
                      <p className="text-3xl md:text-4xl font-bold text-gradient">{caso.metrica}</p>
                      <p className="text-slate-400 text-sm">{caso.metricaLabel}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div variants={fadeInUp} className="text-center mt-10">
            <Button
              asChild
              className="btn-glow bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-base rounded-full"
              data-testid="casos-cta-btn"
            >
              <Link to="/contacto" onClick={handleContactClick}>
                Quiero resultados así en mi empresa
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
